import { ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import {
  CALIBRATE_COLORS,
  CALIBRATE_FONT_FAMILY_ACCENT,
  CALIBRATE_RADIUS_MD,
} from "components/hoc/Theme";
import {
  SHELL_MAIN_CHROME_PADDING_PX,
  VIEWPORT_COMPACT_MAX_MEDIA,
  VIEWPORT_SHORT_MAX_MEDIA,
  PHONE_LANDSCAPE_MEDIA,
} from "config";
import { managementTheme } from "./managementTheme";

interface ManagementPageShellProps {
  /** Visible page title rendered on the grey chrome. */
  title: ReactNode;
  /** Right side of the title row (e.g. a Back button). */
  headerExtra?: ReactNode;
  /** Rendered on the grey chrome between the title row and the white panel. */
  beforePanel?: ReactNode;
  /** Content of the white panel (rendered inside its padded inner box). */
  children: ReactNode;
}

/**
 * A management page's title. The top bar names no page, so a phone in
 * landscape (about 260px of page height) keeps it as one compact line.
 */
export function ManagementPageTitle({
  children,
  gutterBottom = true,
}: {
  children: ReactNode;
  gutterBottom?: boolean;
}) {
  return (
    <Typography
      component="h1"
      sx={{
        fontSize: 24,
        fontWeight: 700,
        mb: gutterBottom ? 1.5 : 0,
        color: "text.primary",
        fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
        flexShrink: 0,
        minWidth: 0,
        // A short viewport spends its height on content, not on chrome.
        [VIEWPORT_SHORT_MAX_MEDIA]: {
          fontSize: 18,
          mb: gutterBottom ? 0.75 : 0,
        },
        [PHONE_LANDSCAPE_MEDIA]: {
          fontSize: 16,
          lineHeight: 1.25,
          mb: gutterBottom ? 0.5 : 0,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * Calibrate management-page chrome: grey backdrop + in-page title + white
 * rounded panel, all under the shared management theme. Extracted from
 * UserManagementPage so every management page renders identically. Expects
 * to fill the viewport (use with `<Layout noScroll>`); scrolling belongs to
 * the panel content.
 */
export default function ManagementPageShell({
  title,
  headerExtra,
  beforePanel,
  children,
}: ManagementPageShellProps) {
  const chromePad = `${SHELL_MAIN_CHROME_PADDING_PX}px`;

  const titleNode = (
    <ManagementPageTitle gutterBottom={!headerExtra}>
      {title}
    </ManagementPageTitle>
  );

  return (
    <ThemeProvider theme={managementTheme}>
      {/* Grey chrome area */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          alignSelf: "stretch",
          display: "flex",
          flexDirection: "column",
          bgcolor: CALIBRATE_COLORS.chromeBackground,
          p: { xs: 1.5, sm: chromePad },
          // Raw tier keys must follow the breakpoint object: emotion keeps the
          // declaration order, and these are max-width/max-height rules.
          [VIEWPORT_COMPACT_MAX_MEDIA]: { p: 1.5 },
          [VIEWPORT_SHORT_MAX_MEDIA]: { py: 1 },
          boxSizing: "border-box",
          minHeight: 0,
        }}
      >
        {headerExtra ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 1.5,
              [VIEWPORT_SHORT_MAX_MEDIA]: { mb: 0.75 },
              flexShrink: 0,
            }}
          >
            {titleNode}
            {headerExtra}
          </Box>
        ) : (
          titleNode
        )}

        {beforePanel}

        {/* White panel */}
        <Box
          sx={(theme) => ({
            bgcolor: "background.paper",
            borderRadius: `${CALIBRATE_RADIUS_MD}px`,
            boxShadow: theme.shadows[2],
            width: "100%",
            flex: 1,
            minHeight: 0,
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
            overflow: "hidden",
          })}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              px: { xs: 1.25, sm: 3 },
              pt: 2,
              pb: 2,
              [VIEWPORT_COMPACT_MAX_MEDIA]: { px: 2 },
              [VIEWPORT_SHORT_MAX_MEDIA]: { pt: 1, pb: 1 },
              boxSizing: "border-box",
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
